import { CSSProperties } from 'react';
import { saloonFonts } from 'styles/saloonTheme';

/**
 * Shared styling for the top-right table-control cluster (Sit Out / Stand Up /
 * Exit). Two presentations, switched on orientation:
 *
 *  - Portrait  → Saloon ".tp-tbtn" pill: full-width column item with an icon
 *               and an uppercase Oswald label, per the Skullcrusher design.
 *  - Landscape → the existing compact icon-only button, left untouched until
 *               the landscape view is redesigned.
 *
 * Per the perf guide: box-shadow is static and the only motion is a
 * transform-only hover (callers gate it behind useSupportsHover).
 */
export type TableControlVariant = 'default' | 'exit' | 'observe' | 'active';

export const tableControlButtonStyle = (
  isPortrait: boolean,
  variant: TableControlVariant = 'default'
): CSSProperties => {
  if (!isPortrait) {
    // Landscape — unchanged compact icon-only chrome.
    return {
      background: variant === 'active' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.04)',
      border: 'none',
      borderRadius: '10px',
      color: variant === 'active' ? '#4CAF50' : 'rgba(255, 255, 255, 0.6)',
      padding: '0.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'inherit',
      minWidth: '44px',
      minHeight: '44px'
    };
  }

  // Portrait — Saloon pill. Text-only and full-width: the cluster container
  // (TableActions) is a fixed-width column with align-items:stretch, so every
  // button renders the same size, per the Skullcrusher design (no icons).
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    // Fill the cluster column (which itself shrinks to the widest label), so
    // Sit Out / Observe / Exit Table all render the SAME width. Compact height
    // and padding keep these secondary controls clear of the table view.
    width: '100%',
    // Vertical padding + minHeight give a comfortable mobile tap target while
    // the content-width column keeps the cluster narrow.
    padding: '0.35rem 0.5rem',
    borderRadius: '4px',
    fontFamily: saloonFonts.numeric, // 'Oswald'
    fontWeight: 600,
    fontSize: '0.55rem',
    // line-height:1 so the text box hugs the glyphs and the flex centering is
    // exact — the inherited line-height otherwise left the label sitting high.
    lineHeight: 1,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textShadow: '0 1px 0 rgba(0,0,0,0.5)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,180,92,0.1)',
    transition: 'transform 0.12s ease',
    minHeight: '30px'
  };

  switch (variant) {
    case 'exit':
      // Danger accent — burnt rust, per the Lobby palette.
      return {
        ...base,
        background: 'linear-gradient(180deg, #c9332a, #6b1a14)',
        border: '1px solid #2a0805',
        color: '#f1e3c7'
      };
    case 'observe':
      return {
        ...base,
        background: 'linear-gradient(180deg, rgba(20,30,48,0.85), rgba(10,18,32,0.85))',
        border: '1px dashed rgba(232,130,31,0.45)',
        color: '#d9c79e'
      };
    case 'active':
      // Toggled-on state (sat out / standing up) — saloon green.
      return {
        ...base,
        background: 'linear-gradient(180deg, rgba(76,175,80,0.5), rgba(30,90,46,0.6))',
        border: '1px solid rgba(76,175,80,0.6)',
        color: '#f1e3c7'
      };
    default:
      return {
        ...base,
        background: 'linear-gradient(180deg, rgba(74,35,10,0.65), rgba(40,18,6,0.75))',
        border: '1px solid rgba(232,130,31,0.35)',
        color: '#f1e3c7'
      };
  }
};

export const tableControlIconStyle = (isPortrait: boolean): CSSProperties => ({
  fontSize: isPortrait ? '0.85rem' : '1.4rem',
  opacity: isPortrait ? 0.85 : 1,
  flexShrink: 0
});
